// src/components/Sidebar.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ssoLoginApi } from "../api/carbonApi";
import { useRole } from "../auth";
import msalInstance, { loginRequest } from "../auth/msalInstance";
import { navSectionsForRole } from "../config/routes";
import { useAuthContext } from "../context/AuthContext";
import ChangePasswordModal from "../features/auth/components/ChangePasswordModal";
import { clearStaleMsalInteractionStatus } from '../features/auth/model/msalInteraction';
import { Avatar } from "../shared/components/Badges";
import BrandMark from "../shared/components/BrandMark";
import Icon from "../shared/components/Icon";
import '../styles/components/sidebar.css';

export default function Sidebar({
  collapsed = false, onToggleCollapse, mobile = false, onNavigate,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { auth, login, logout } = useAuthContext();
  const { can, canChangePassword } = useRole();

  const [showChangePwd, setShowChangePwd] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reauthing, setReauthing] = useState(false);
  const menuRef = useRef(null);

  // Derive available navigation from the current user's role.
  const navSections = navSectionsForRole(can);
  const isSso = auth?.auth_method === "sso";

  useEffect(() => {
    if (!menuOpen) return;
    const handler = e => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  async function handleLogout() {
    setMenuOpen(false);
    const wasSso = auth?.auth_method === "sso";
    logout();
    if (wasSso) {
      try {
        await msalInstance.logoutPopup({ postLogoutRedirectUri: window.location.origin });
      } catch {
        // MSAL logout failed silently — local session already cleared by logout()
      }
    }
  }

  async function handleReauthSso() {
    if (reauthing) return;
    setReauthing(true);
    try {
      const response = await msalInstance.loginPopup(loginRequest);
      const data = await ssoLoginApi(response.accessToken);
      login({ user: data.user, token: data.access_token, remember: auth?.remember ?? true });
      setMenuOpen(false);
    } catch (err) {
      if (err?.errorCode === "interaction_in_progress") {
        clearStaleMsalInteractionStatus();
      }
      // Leave menu open so the user can retry
    } finally {
      setReauthing(false);
    }
  }

  const displayName = auth?.user?.full_name || auth?.name || t("sidebar.defaultUser");
  const agentInitials = displayName.split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const agentAvatar = { initials: agentInitials, hue: 220 };
  const userRole = auth?.user?.role || "";

  const renderNavRoute = (route) => {
    const routeLabel = t(route.navKey);
    return (
      <button
        key={route.path}
        className={`nav-item ${location.pathname === route.path ? "active" : ""}${collapsed ? " has-tooltip" : ""}`}
        onClick={() => { navigate(route.path); onNavigate?.(); }}
        aria-label={collapsed ? routeLabel : undefined}
        data-tooltip={routeLabel}
      >
        <Icon name={route.icon} size={collapsed ? 19 : 16} />
        {!collapsed && <span>{routeLabel}</span>}
      </button>
    );
  };

  return (
    <aside className={`sidebar${collapsed ? " sidebar--collapsed" : ""}${mobile ? " sidebar--mobile" : ""}`}>
      <div className="brand">
        <div className="brand-mark">
          <BrandMark size={32} />
        </div>
        {!collapsed && (
          <div className="brand-text">
            <div className="brand-name">{t("breadcrumb.appName")}</div>
            <div className="brand-sub">{t("sidebar.brandSubtitle")}</div>
          </div>
        )}
        {!mobile && !collapsed && onToggleCollapse && (
          <button
            type="button"
            className="icon-btn sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={t("sidebar.collapse")}
            title={t("sidebar.collapse")}
          >
            <Icon name="chevron-left" size={14} />
          </button>
        )}
      </div>

      {!mobile && collapsed && onToggleCollapse && (
        <div className="sidebar-expand-row">
          <button
            type="button"
            className="icon-btn sidebar-collapse-btn"
            onClick={onToggleCollapse}
            aria-label={t("sidebar.expand")}
            title={t("sidebar.expand")}
          >
            <Icon name="chevron-right" size={14} />
          </button>
        </div>
      )}

<nav className="nav">
        {navSections.map(section => (
          <React.Fragment key={section.id || section.label}>
            {section.id ? (
              <div className={`nav-box nav-box--${section.id}`}>
                {!collapsed && <div className="nav-box__title">{t(section.labelKey)}</div>}
                {section.items.map(renderNavRoute)}
              </div>
            ) : (
              <>
                {!collapsed && <div className="nav-section">{t(section.labelKey)}</div>}
                {section.items.map(renderNavRoute)}
              </>
            )}
          </React.Fragment>
        ))}
      </nav>

      <div className="sidebar-foot" ref={menuRef}>
        <div className="user-card">
          <button
            type="button"
            className={`avatar-trigger${collapsed ? " has-tooltip" : ""}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-label={t("sidebar.accountMenu")}
            aria-haspopup="true"
            aria-expanded={menuOpen}
            data-tooltip={displayName}
          >
            <Avatar agent={agentAvatar} size={32} />
          </button>
          {!collapsed && (
            <>
              <div className="user-card__info">
                <div className="user-name">{displayName}</div>
                <div className="user-role">{userRole}</div>
              </div>
              <button
                type="button"
                className="icon-btn"
                aria-label={t("sidebar.accountMenu")}
                aria-haspopup="true"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen(o => !o)}
              >
                <Icon name="more" size={15} />
              </button>
            </>
          )}

          {menuOpen && (
            <div className={`account-menu__panel${collapsed ? " account-menu__panel--rail" : ""}`}>
              {canChangePassword && (
                <button
                  type="button"
                  className="account-menu__item"
                  onClick={() => { setMenuOpen(false); setShowChangePwd(true); }}
                >
                  <Icon name="key" size={15} />
                  <span>{t("sidebar.changePassword")}</span>
                </button>
              )}
              {isSso && (
                <button
                  type="button"
                  className="account-menu__item"
                  onClick={handleReauthSso}
                  disabled={reauthing}
                >
                  <Icon name="refresh" size={15} />
                  <span>{reauthing ? t("sidebar.reauthenticating") : t("sidebar.reauthSso")}</span>
                </button>
              )}
              <button
                type="button"
                className="account-menu__item account-menu__item--danger"
                onClick={handleLogout}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                </svg>
                <span>{t("sidebar.signOut")}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {showChangePwd && (
        <ChangePasswordModal
          onClose={() => setShowChangePwd(false)}
          onLogout={logout}
        />
      )}
    </aside>
  );
}
